<template>
  <div class="view-source">
    <template v-if="!source">
      <div class="view-source-missing">This source is currently unavailable.</div>
    </template>
    <template v-else>
      <figure v-if="presentation === 'quote'" class="view-quote">
        <blockquote>
          <p v-for="(text, index) in selectedParts" :key="`${index}-${text}`">{{ text }}</p>
        </blockquote>
        <figcaption>{{ sourceTitle }}</figcaption>
      </figure>
      <ViewCitation
        v-else-if="presentation === 'cite'"
        :source="source"
        :texts="selectedParts"
        @open-source="$emit('open-source', source)"
      />
      <section v-else-if="presentation === 'component'" class="view-origin-frame">
        <div class="origin-content">
          <ViewOriginalBlock
            :source="source"
            :block="block"
            @source-error="$emit('source-error', block.source_id)"
          />
        </div>
        <ViewCitation footer :source="source" @open-source="$emit('open-source', source)" />
      </section>
      <div v-else-if="presentation === 'excerpt'" class="view-excerpt">
        <p class="view-source-label">{{ sourceTitle }}</p>
        <p v-for="(text, index) in selectedParts" :key="`${index}-${text}`">{{ text }}</p>
      </div>
      <div v-else-if="presentation === 'card'" class="view-card">
        <div class="view-card-kicker">{{ kindLabel }}</div>
        <h3 class="view-card-title">{{ sourceTitle }}</h3>
        <p v-if="entityMeta" class="view-card-meta">{{ entityMeta }}</p>
        <p v-if="entityDescription" class="view-card-desc">{{ entityDescription }}</p>
        <div v-if="source.media && source.media.length" class="view-card-media">
          <img :data-enlarge="source.entity?.mediaMode === 'logo' ? null : ''" :src="source.media[0].src" :alt="sourceTitle" loading="lazy" decoding="async" @error="$emit('source-error', block.source_id)">
        </div>
      </div>
      <div v-else-if="presentation === 'media'" class="view-media">
        <p class="view-source-label">{{ sourceTitle }}</p>
        <img data-enlarge v-for="m in source.media" :key="m.src" :src="m.src" :alt="sourceTitle" loading="lazy" decoding="async" @error="$emit('source-error', block.source_id)">
      </div>
      <section v-else-if="presentation === 'interactive' || presentation === 'embed'" class="view-origin-frame view-rich-wrap">
        <div class="origin-content">
          <ViewArchivedSnapshot
            v-if="source.snapshot_html && !renderLiveRichBlock"
            :html="source.snapshot_html"
            :block-id="block.source_id"
            :snapshot-source="source"
            :current-source="currentSource"
          />
          <ViewRichBlock v-else :block-id="block.source_id" :source="source" />
          <p v-if="renderLiveRichBlock && richSourceChanged" class="snapshot-state"><span aria-hidden="true">◷</span> Shown as it appeared when this View was created</p>
        </div>
        <ViewCitation footer :source="source" @open-source="$emit('open-source', source)" />
      </section>
      <div v-if="!['cite', 'component', 'interactive', 'embed'].includes(presentation)" class="view-provenance">
        <button v-if="provenance.route" type="button" @click="$emit('open-source', source)">
          Read original in {{ sourcePageLabel(provenance) }}
          <ArrowUpRightIcon />
        </button>
        <span v-else>{{ sourcePageLabel(provenance) }}</span>
      </div>
    </template>
  </div>
</template>

<script>
import ViewRichBlock from './ViewRichBlock.vue';
import ViewOriginalBlock from './ViewOriginalBlock.vue';
import ViewCitation from './ViewCitation.vue';
import ViewArchivedSnapshot from './ViewArchivedSnapshot.vue';
import { sourceDisplayTitle } from '../../utils/askRetrieval';
import { sourceFingerprint } from '../../utils/viewSnapshotCompare';
import { decodeViewText } from '../../utils/viewText';
import { sourcePageLabel } from '../../utils/viewSourceTarget';

export default {
  name: 'ViewSourceBlock',
  components: { ViewArchivedSnapshot, ViewCitation, ViewOriginalBlock, ViewRichBlock },
  props: {
    block: {
      type: Object,
      required: true,
    },
    source: {
      type: Object,
      default: null,
    },
    currentSource: {
      type: Object,
      default: null,
    },
  },
  emits: ['open-source', 'source-error'],
  data() {
    return { richSourceChanged: false };
  },
  computed: {
    selectedParts() {
      if (!this.source) return [];
      const parts = Array.isArray(this.source.parts) ? this.source.parts : [];
      const selected = !this.block.part_indices || !this.block.part_indices.length
        ? (parts.length ? [parts[0]] : [])
        : this.block.part_indices.map((i) => parts[i]).filter((text) => text != null);
      return selected.map(decodeViewText);
    },
    presentation() {
      return this.block.presentation || 'excerpt';
    },
    sourceTitle() {
      return sourceDisplayTitle(this.source);
    },
    kindLabel() {
      const labels = {
        prose: 'From the site',
        fragment: 'Fragment',
        project: 'Project',
        experience: 'Experience',
        education: 'Education',
        publication: 'Research',
      };
      return labels[this.source?.view_kind] || 'Source';
    },
    provenance() {
      if (!this.source) return { page: 'Original source', route: null };
      return {
        page: this.source.page || this.sourceTitle,
        route: this.source.route || null,
        anchor: this.source.anchor || '',
      };
    },
    entity() {
      return this.source && this.source.entity;
    },
    entityMeta() {
      if (!this.entity) return '';
      if (this.entity.kind === 'publication') return [this.entity.period, this.entity.status].filter(Boolean).join(' · ');
      if (this.entity.kind === 'project') return this.entity.period || '';
      if (this.entity.kind === 'experience') return [this.entity.organisation, this.entity.period].filter(Boolean).join(' · ');
      if (this.entity.kind === 'fragment') return this.entity.teaser || '';
      return '';
    },
    entityDescription() {
      return this.source && this.source.parts && this.source.parts[0] ? this.source.parts[0] : '';
    },
    renderLiveRichBlock() {
      const blockId = this.block?.source_id || '';
      return [
        'interactive.travels.globe',
        'interactive.about.exploration-paths',
        'interactive.search.system-paths',
        'interactive.search.embedding-map',
        'interactive.search.retrieval-lab',
        'interactive.search.view-composer',
      ].includes(blockId) || blockId.startsWith('interactive.travels.place.');
    },
  },
  watch: {
    source: 'compareLiveRichSource',
    currentSource: 'compareLiveRichSource',
  },
  mounted() {
    this.compareLiveRichSource();
  },
  methods: {
    sourcePageLabel,
    async compareLiveRichSource() {
      if (!this.renderLiveRichBlock) return;
      if (!this.currentSource) {
        this.richSourceChanged = true;
        return;
      }
      const current = await sourceFingerprint(this.currentSource);
      const saved = this.source?.source_fingerprint || await sourceFingerprint(this.source);
      this.richSourceChanged = current !== saved;
    },
  },
};
</script>

<style scoped>
.view-source {
  position: relative;
  isolation: isolate;
  margin: 1.25rem 0;
}
/* Keep the source content and its return link visually connected. */
.view-source::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: rgba(var(--site-accent-rgb), 0.055);
  opacity: 0;
  transition: opacity 160ms ease;
}
.view-source:has(.citation-source:focus-visible)::after,
.view-source:has(.view-citation.is-clickable:focus-visible)::after,
.view-source:has(.view-citation.is-clickable:active)::after,
.view-source:has(.view-provenance button:focus-visible)::after {
  opacity: 1;
}
@media (hover: hover) {
  .view-source:has(.citation-source:hover)::after,
  .view-source:has(.view-citation.is-clickable:hover)::after,
  .view-source:has(.view-provenance button:hover)::after {
    opacity: 1;
  }
}
@media (prefers-reduced-motion: reduce) {
  .view-source::after { transition: none; }
}
.snapshot-state {
  margin: 0.85rem 0 0;
  color: #858585;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.64rem;
  line-height: 1.4;
}
.snapshot-state span { color: var(--site-accent); }
body.dark-mode .snapshot-state { color: #999; }

.view-origin-frame {
  margin: 1.5rem 0;
  border: 0;
  background: rgba(var(--site-accent-rgb), 0.052);
}

.origin-content {
  padding: clamp(1rem, 3vw, 1.4rem);
  overflow: hidden;
  container-type: inline-size;
}

.origin-content > * {
  width: 100%;
  zoom: 0.94;
}

.view-source-missing {
  padding: 0.8rem 1rem;
  border-left: 3px solid #b3893d;
  background: rgba(0, 0, 0, 0.03);
  color: #767676;
}

.view-quote {
  margin: 0;
  padding: 0;
}

.view-quote blockquote {
  position: relative;
  margin: 0;
  padding: 0 0 0 1.45rem;
  border-left: 0.16rem solid var(--site-accent);
}
.view-quote p {
  margin: 0.4rem 0;
  font-size: clamp(1.1rem, 2.2vw, 1.26rem);
  line-height: 1.62;
  letter-spacing: -0.008em;
  color: #2f2f2f;
}
.view-quote figcaption,
.view-source-label {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.67rem;
  letter-spacing: 0.06em;
  color: #838383;
}
.view-quote figcaption { margin: 0.7rem 0 0 1.6rem; }
.view-source-label {
  margin: 0 0 0.7rem !important;
  font-weight: 600;
  text-transform: uppercase;
}

.view-excerpt p {
  margin: 0.5rem 0;
  line-height: 1.72;
  color: #4f4f4f;
}
.view-excerpt p:not(.view-source-label) {
  max-width: 42rem;
  font-size: 1rem;
  text-wrap: pretty;
}

.view-card {
  padding: 1.2rem 1.25rem;
  border: 0;
  border-radius: 2px;
  background: rgba(250, 247, 242, 0.74);
}

.view-card-kicker {
  margin: 0 0 0.35rem;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.66rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #767676;
}

.view-card-title {
  margin: 0 0 0.45rem;
  font-size: 1.2rem;
  line-height: 1.25;
  letter-spacing: -0.012em;
}

.view-card-meta {
  margin: 0 0 0.45rem;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.8rem;
  color: #767676;
}

.view-card-desc {
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.62;
  color: #5c5c5c;
}

.view-card-media img {
  width: auto;
  max-width: 100%;
  height: auto;
  max-height: 240px;
  object-fit: contain;
  margin-top: 0.8rem;
  border: 1px solid #e7e1da;
  border-radius: 0;
  background: #fff;
}

.view-origin-frame :deep(.scroll-container img),
.view-origin-frame :deep(.proof-gallery img) {
  border-radius: 0 !important;
}

.view-media {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem;
}
.view-media .view-source-label { grid-column: 1 / -1; }

.view-media img {
  width: 100%;
  border: 1px solid #e7e1da;
  border-radius: 0;
  background: #fff;
}

.view-provenance {
  margin-top: 0.65rem;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.72rem;
  color: #767676;
}

.view-provenance button {
  appearance: none;
  border: 0;
  border-bottom: 1px solid currentColor;
  background: transparent;
  color: inherit;
  font: inherit;
  padding: 0 0 2px;
  cursor: pointer;
  transition: color 150ms ease, transform 100ms ease-out;
}
.view-provenance button:active { transform: scale(0.98); }

body.dark-mode .view-source-missing {
  background: rgba(255, 255, 255, 0.04);
  color: #a1a1a1;
}

body.dark-mode .view-origin-frame {
  background: rgba(var(--site-accent-rgb), 0.055);
}

body.dark-mode .view-quote p {
  color: #d7d7d7;
}

body.dark-mode .view-excerpt p { color: var(--site-text-secondary); }
body.dark-mode .view-card {
  border-color: #2e2e2e;
  background: rgba(var(--site-accent-shade-rgb), 0.55);
}
body.dark-mode .view-card-desc { color: var(--site-text-secondary); }
body.dark-mode .view-card-media img,
body.dark-mode .view-media img { background: #141414; }
body.dark-mode .view-provenance,
body.dark-mode .view-card-kicker,
body.dark-mode .view-card-meta { color: var(--site-text-tertiary); }
body.dark-mode .view-quote figcaption,
body.dark-mode .view-source-label { color: var(--site-text-tertiary); }

@media (max-width: 520px) {
  .origin-content { padding: 0.85rem; }
}
</style>
